import React from 'react';
import Select, { StylesConfig, components as SelectComponents } from 'react-select';
import { useFormContext } from 'react-hook-form';
import { countryList } from '../../data/data';
import InputFieldV2 from './InputFieldV2';

interface CountryOption {
    name: string;
    flag: string;
    code: string;
    dial_code: string;
    value: string;
    label: string;
}

export interface PhoneInputV2Props {
    name: string;
    countryName?: string;
    label?: string;
    placeholder?: string;
    isDisabled?: boolean;
    rules?: object;
    containerStyle?: React.CSSProperties;
    maxLength?: any
}

const selectStyles: StylesConfig<CountryOption, false> = {
    control: (base, state) => ({
        ...base,
        height: '48px',
        minHeight: '48px',
        borderRadius: '0',
        border: '1px solid #d0d0d0',
        borderRight: 'none',
        boxShadow: 'none',
        background: '#ffffff',
        cursor: 'pointer',
        transition: 'border-color 0.2s ease',
        '&:hover': { borderColor: '#d0d0d0' },
        ...(state.isFocused && { borderColor: '#ffd500', outline: 'none' }),
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0 4px 0 12px',
        flexWrap: 'nowrap',
    }),
    singleValue: (base) => ({
        ...base,
        margin: 0,
        maxWidth: '100%',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: '0 8px 0 2px',
        color: '#a0aec0',
        '&:hover': { color: '#718096' },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
        overflow: 'hidden',
        zIndex: 300,
        minWidth: '260px',
    }),
    menuList: (base) => ({
        ...base,
        padding: '6px 0',
        maxHeight: '220px',
    }),
    option: (base, state) => ({
        ...base,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '9px 14px',
        fontSize: '13px',
        cursor: 'pointer',
        backgroundColor: state.isSelected
            ? '#fffdf0'
            : state.isFocused
                ? '#f7f8fa'
                : '#ffffff',
        color: state.isSelected ? '#1a202c' : '#2d3748',
        fontWeight: state.isSelected ? 600 : 400,
        '&:active': { backgroundColor: '#fffdf0' },
    }),
    input: (base) => ({
        ...base,
        fontSize: '13px',
        color: '#2d3748',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#a8a8a8',
        fontSize: '13px',
    }),
};

const formatOptionLabel = (option: CountryOption, { context }: { context: string }) => {
    if (context === 'menu') {
        return (
            <div className="phone-input__option">
                <span className="phone-input__option-flag">{option.flag}</span>
                <span className="phone-input__option-name">{option.name}</span>
                <span className="phone-input__option-dial">{option.dial_code}</span>
            </div>
        );
    }
    return (
        <div className="phone-input__selected">
            <span className="phone-input__selected-flag">{option.flag}</span>
            <span className="phone-input__selected-dial">{option.dial_code}</span>
        </div>
    );
};

const PhoneInputV2: React.FC<PhoneInputV2Props> = ({
    name,
    countryName = 'dial_code',
    label,
    placeholder = '00 000 000',
    isDisabled = false,
    rules,
    containerStyle,
    maxLength
}) => {
    const { setValue, watch } = useFormContext();

    const selectedValue = watch(countryName);

    const selectedOption: CountryOption =
        selectedValue && typeof selectedValue === 'object'
            ? selectedValue
            : countryList.find((c) => c.dial_code === selectedValue) ?? countryList[0];

    const handleCountryChange = (option: CountryOption | null) => {
        if (option) setValue(countryName, option, { shouldValidate: true });
    };

    return (
        <div className="phone-input" style={containerStyle}>
            {label && (
                <label className="phone-input__label">{label}</label>
            )}

            <div className="phone-input__wrapper">

                <div className="phone-input__country">
                    <Select<CountryOption, false>
                        name={countryName}
                        options={countryList as CountryOption[]}
                        value={selectedOption}
                        onChange={handleCountryChange}
                        styles={selectStyles}
                        isDisabled={isDisabled}
                        isSearchable
                        placeholder=""
                        formatOptionLabel={formatOptionLabel}
                    />
                </div>

                <div className="phone-input__number">
                    <InputFieldV2
                        name={name}
                        type="tel"
                        placeholder={placeholder}
                        rules={rules}
                        isDisabled={isDisabled}
                        maxLength={maxLength || 10}
                    />
                </div>
            </div>
        </div>
    );
};

export default PhoneInputV2;